import { useQueryClient } from "@tanstack/react-query";
import {
  Eye,
  Info,
  FileCheck,
  EyeOff,
  Lock,
  Loader2,
  ExternalLink,
  ArrowLeftRight,
  MessageSquareWarning,
  Truck,
} from "lucide-react";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  getListCardsPaymentAccountCardAllGetQueryKey,
  useLockCardPaymentAccountLockPost,
  useUnlockCardPaymentAccountUnlockPost,
} from "@/api-v3";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { isCardStatusActivationRequired } from "@/features/card/utils/card-status";
import { useContractLabel } from "@/features/legal";
import {
  findPaymentMethodForCard,
  findPhysicalCardMethodForVirtualCard,
  getOrderablePhysicalCardType,
  useAvailablePaymentMethods,
} from "@/features/payment-methods";
import { usePaymentMethods } from "@/features/payments";
import {
  canActivate,
  hasDisputeContact,
  type PaymentAccountCard,
} from "@/features/payments/types/payment-account-cards";
import { useToast } from "@/hooks/use-toast.ts";
import { cn, noop } from "@/lib/utils";

interface CardActionsProps {
  card: PaymentAccountCard;
  visibilityHandler: {
    toggleCardNumberMask: () => void;
    isNumberVisible: boolean;
  };
}

export const CardActions = (props: CardActionsProps) => {
  const { card, visibilityHandler } = props;
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { cards } = usePaymentMethods();
  const { availablePaymentMethods } = useAvailablePaymentMethods();
  const contractLabel = useContractLabel();
  // The agreements shown for a card are the ones its own programme requires,
  // resolved from the matching payment method rather than a global scope.
  const cardAgreements =
    findPaymentMethodForCard(
      availablePaymentMethods,
      card.card_type,
      card.provider
    )?.legal_documents ?? [];
  // Every physical Card Type arrives as plastic that has to be activated, and
  // the actions that need a live card stay shut until it is.
  const hasDisabledActions =
    canActivate(card.card_type) && isCardStatusActivationRequired(card.status);

  // Only a Virtual Card and a Virtual Stipend Card have plastic to order, and
  // it is this card's own sibling, matched on Card Type and provider: a Stripe
  // Virtual Stipend Card offers a Physical Stipend Card, never the Highnote
  // Physical Card. Offered while that sibling is on offer and not already held.
  const orderablePhysicalCardType = getOrderablePhysicalCardType(
    card.card_type
  );
  const physicalCardMethod = findPhysicalCardMethodForVirtualCard(
    availablePaymentMethods,
    card.card_type,
    card.provider
  );
  const alreadyHoldsPhysicalCard = cards.some(
    (held) => held.card_type === orderablePhysicalCardType
  );
  const canOrderPhysicalCard =
    Boolean(orderablePhysicalCardType) &&
    Boolean(physicalCardMethod) &&
    !alreadyHoldsPhysicalCard;

  const { mutateAsync: lockAsync, isPending: isLocking } =
    useLockCardPaymentAccountLockPost({
      mutation: {
        onError: (error) => {
          console.error(error);
          toast({
            description: t("error_somethingWentWrong"),
          });
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getListCardsPaymentAccountCardAllGetQueryKey(),
          });
        },
      },
    });

  const { mutateAsync: unlockAsync, isPending: isUnlocking } =
    useUnlockCardPaymentAccountUnlockPost({
      mutation: {
        onError: (error) => {
          console.error(error);
          toast({
            description: t("error_somethingWentWrong"),
          });
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getListCardsPaymentAccountCardAllGetQueryKey(),
          });
        },
      },
    });

  const toggleCardLock = useCallback(async () => {
    const method = card.card_type;
    const provider = card.provider;

    if (card.status.toLowerCase() === "suspended") {
      await unlockAsync({ params: { method, provider } });
    } else if (card.status.toLowerCase() === "active") {
      await lockAsync({ params: { method, provider } });
    }
  }, [lockAsync, card, unlockAsync]);

  return (
    <>
      <ul className="bg-white rounded overflow-hidden">
        <li>
          <ListItemButton
            onClick={visibilityHandler.toggleCardNumberMask}
            disabled={hasDisabledActions}
          >
            <div className="min-w-9 text-muted-foreground inline-flex shrink-0">
              {visibilityHandler.isNumberVisible ? (
                <EyeOff className="size-5" />
              ) : (
                <Eye className="size-5" />
              )}
            </div>
            <div className="flex-1 flex-basis-auto min-w-0 my-1">
              <span className="block">
                {visibilityHandler.isNumberVisible
                  ? t("card_hide")
                  : t("card_show")}
              </span>
            </div>
          </ListItemButton>
        </li>
        <li>
          <Separator />
        </li>
        <li>
          <ListItemButton
            onClick={() => navigate(`/card/${card.type}/transactions`)}
            disabled={hasDisabledActions}
          >
            <div className="min-w-9 text-muted-foreground inline-flex shrink-0">
              <ArrowLeftRight className="size-5" />
            </div>
            <div className="flex-1 flex-basis-auto min-w-0 my-1">
              <span className="block">{t("card_balance_history")}</span>
            </div>
          </ListItemButton>
        </li>
        <li>
          <Separator />
        </li>
        <li>
          <ListItemButton onClick={() => navigate(`/card/faq`)}>
            <div className="min-w-9 text-muted-foreground inline-flex shrink-0">
              <Info className="size-5" />
            </div>
            <div className="flex-1 flex-basis-auto min-w-0 my-1">
              <span className="block">{t("card_about_your_card")}</span>
            </div>
          </ListItemButton>
        </li>
        {hasDisputeContact(card) && (
          <>
            <li>
              <Separator />
            </li>
            <li>
              {/* Stays enabled while a card awaits activation: the complaint channel must not close. */}
              <ListItemButton onClick={() => navigate("/card/dispute")}>
                <div className="min-w-9 text-muted-foreground inline-flex shrink-0">
                  <MessageSquareWarning className="size-5" />
                </div>
                <div className="flex-1 flex-basis-auto min-w-0 my-1">
                  <span className="block">{t("card_disputeOrComplaint")}</span>
                </div>
              </ListItemButton>
            </li>
          </>
        )}
        {canOrderPhysicalCard && (
          <>
            <li>
              <Separator />
            </li>
            <li>
              <ListItemButton
                onClick={() =>
                  navigate("/card/physical/order", {
                    state: { cardId: card.id },
                  })
                }
              >
                <div className="min-w-9 text-muted-foreground inline-flex shrink-0">
                  <Truck className="size-5" />
                </div>
                <div className="flex-1 flex-basis-auto min-w-0 my-1">
                  <span className="block">{t("card_order_physical")}</span>
                </div>
              </ListItemButton>
            </li>
          </>
        )}
        {cardAgreements
          .filter((document) => document.version.document_url)
          .map((document) => (
            <React.Fragment key={document.id}>
              <li>
                <Separator />
              </li>
              <li>
                <ListItemLink
                  href={document.version.document_url ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="min-w-9 text-muted-foreground inline-flex shrink-0">
                    <FileCheck className="size-5" />
                  </div>
                  <div className="flex items-center gap-x-2 flex-1 flex-basis-auto min-w-0 my-1">
                    <span className="block">
                      {contractLabel(document.title)}
                    </span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                </ListItemLink>
              </li>
            </React.Fragment>
          ))}
      </ul>
      <section className="mt-4">
        <ul className="bg-white rounded overflow-hidden">
          <li>
            <ListItemButton
              className="cursor-default hover:bg-transparent"
              disabled={hasDisabledActions}
            >
              <div className="min-w-9 text-red-500 inline-flex shrink-0">
                <Lock className="size-5" />
              </div>
              <div className="flex-1 flex-basis-auto min-w-0 my-1">
                <span className="block ">{t("card_lock_card")}</span>
              </div>

              <div className="flex items-center justify-end">
                {isLocking || isUnlocking ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Switch
                    className="data-[state=checked]:bg-red-500"
                    checked={card.status.toLowerCase() === "suspended"}
                    onCheckedChange={toggleCardLock}
                  />
                )}
              </div>
            </ListItemButton>
          </li>
        </ul>
      </section>
    </>
  );
};

interface ListItemLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  disabled?: boolean;
}

const ListItemLink = (props: ListItemLinkProps) => {
  const { disabled, className, children, ...rest } = props;
  return (
    <a
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={disabled ? noop : undefined}
      className={cn(
        "flex items-center px-4 py-2 min-w-0 cursor-pointer select-none align-middle transition-colors duration-200 hover:bg-neutral-100",
        disabled && "opacity-50 cursor-not-allowed pointer-events-none",
        className
      )}
      {...(disabled ? { "aria-disabled": true } : {})}
      {...rest}
    >
      {children}
    </a>
  );
};

interface ListItemButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
}

const ListItemButton = (props: ListItemButtonProps) => {
  const { disabled, className, children, ...rest } = props;
  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={disabled ? noop : undefined}
      className={cn(
        "flex items-center px-4 py-2 min-w-0 cursor-pointer select-none align-middle transition-colors duration-200 hover:bg-neutral-100",
        disabled && "opacity-50 cursor-not-allowed pointer-events-none",
        className
      )}
      {...(disabled ? { "aria-disabled": true } : {})}
      {...rest}
    >
      {children}
    </div>
  );
};
